import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AreaSpark, Bars, Donut, Waterfall } from "./charts";
import { IllustrativeBadge } from "./bits";

const TABS = {
    growth: { label: "Growth", series: [42, 48, 45, 56, 61, 58, 67, 72, 78], color: "#1E88F7" },
    margin: { label: "Margin", series: [30, 32, 31, 34, 36, 35, 38, 40, 44], color: "#3977F6" },
    recovery: { label: "Recovery", series: [12, 18, 16, 24, 30, 28, 36, 42, 51], color: "#1E88F7" },
};

const CHANNELS = [
    { n: "Paid Search", v: 74, c: "rgba(30,136,247,0.7)" },
    { n: "LinkedIn", v: 58, c: "rgba(57,119,246,0.7)" },
    { n: "Outbound", v: 46, c: "rgba(247,248,246,0.35)" },
    { n: "Retargeting", v: 31, c: "rgba(247,248,246,0.25)" },
];

const KPIS = [
    { l: "Recoverable Revenue", v: "$1.1M" },
    { l: "Potential Vendor Savings", v: "$846K" },
    { l: "Acquisition Cost", v: "−12%" },
    { l: "Sales Velocity", v: "+22%" },
];

const ExecDashboard = ({ compact = false }) => {
    const [tab, setTab] = useState("growth");
    const t = TABS[tab];

    return (
        <div className="rounded-2xl border border-white/10 bg-ink2 p-5 sm:p-8" data-testid="executive-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.24em] text-cloud">
                    <span className="inline-block size-1.5 rounded-full bg-vantera animate-blink" aria-hidden="true" />
                    Vantera Executive Intelligence
                </p>
                <IllustrativeBadge dark />
            </div>

            <div className="mt-8 grid gap-8 lg:grid-cols-[1.6fr_1fr]">
                <div>
                    <div className="flex gap-2" role="tablist" aria-label="Dashboard view">
                        {Object.entries(TABS).map(([id, v]) => (
                            <button
                                key={id}
                                role="tab"
                                aria-selected={tab === id}
                                data-testid={`dashboard-tab-${id}`}
                                onClick={() => setTab(id)}
                                className={`rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${tab === id ? "border-vantera text-vantera" : "border-white/15 text-cloud hover:text-ivory"}`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                    <AnimatePresence mode="wait">
                        <motion.div key={tab} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                            <AreaSpark series={t.series} color={t.color} id={`exec-${tab}`} height={190} />
                        </motion.div>
                    </AnimatePresence>
                    <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {KPIS.map((k, i) => (
                            <motion.div key={k.l} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }} className="rounded-lg border border-white/10 bg-white/[0.03] p-3.5" data-testid={`dashboard-kpi-${i}`}>
                                <p className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-cloud">{k.l}</p>
                                <p className="mt-1.5 font-display text-lg font-extrabold text-ivory">{k.v}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <div className="space-y-8 border-white/10 lg:border-l lg:pl-8">
                    <Donut pct={64} label="Gross Margin" sub="Illustrative gross margin after lever deployment" />
                    <div>
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-cloud">Channel efficiency index</p>
                        <div className="mt-4">
                            <Bars items={CHANNELS} dark height={120} />
                        </div>
                    </div>
                </div>
            </div>

            {!compact && (
                <div className="mt-10 border-t border-white/10 pt-8">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cloud">Gross profit bridge — illustrative</p>
                    <div className="mt-6">
                        <Waterfall
                            dark
                            steps={[
                                { n: "REVENUE", delta: 40 },
                                { n: "COGS", delta: -22 },
                                { n: "VENDOR SAVINGS", delta: 8 },
                                { n: "MKTG EFFICIENCY", delta: 6 },
                                { n: "GROSS PROFIT", delta: 0 },
                            ]}
                        />
                    </div>
                </div>
            )}

            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.16em] text-cloud/70">
                Illustrative data for demonstration only — not actual client results.
            </p>
        </div>
    );
};

export default ExecDashboard;